import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Plus,
  Trash,
  PencilSimple,
  SpinnerGap,
  ShieldCheck,
  WifiHigh,
  Desktop,
  Warning,
  Clock,
  CheckCircle,
  XCircle,
  Globe,
  LockKey,
  UsersThree,
  Columns,
  Eye,
  EyeSlash,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessControlPage = () => {
  const [activeTab, setActiveTab] = useState('permissions');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">Access Control</h1>
        <p className="text-slate-500 mt-1">Manage role permissions, page/field visibility, custom roles, custom fields, and security restrictions</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="permissions" className="flex items-center gap-2" data-testid="tab-permissions">
            <LockKey size={16} /> Role Permissions
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="tab-roles">
            <UsersThree size={16} /> Roles
          </TabsTrigger>
          <TabsTrigger value="custom-fields" className="flex items-center gap-2" data-testid="tab-custom-fields">
            <Columns size={16} /> Custom Fields
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2" data-testid="tab-security">
            <ShieldCheck size={16} /> Security
          </TabsTrigger>
        </TabsList>

        <TabsContent value="permissions"><RolePermissionsTab /></TabsContent>
        <TabsContent value="roles"><RolesTab /></TabsContent>
        <TabsContent value="custom-fields"><CustomFieldsTab /></TabsContent>
        <TabsContent value="security"><SecurityTab /></TabsContent>
      </Tabs>
    </div>
  );
};

/* ===== Tab 1: Role Permissions Matrix ===== */
const RolePermissionsTab = () => {
  const [roles, setRoles] = useState([]);
  const [pageRegistry, setPageRegistry] = useState({});
  const [rbacConfig, setRbacConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [expandedPage, setExpandedPage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, registryRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/rbac/roles`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/rbac/page-registry`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/rbac/config`, { withCredentials: true }),
      ]);
      setRoles(rolesRes.data);
      setPageRegistry(registryRes.data.pages || {});
      setRbacConfig(configRes.data || {});
      if (!selectedRole && rolesRes.data.length > 0) {
        // Default to first non-admin role
        const firstNonAdmin = rolesRes.data.find(r => !['superadmin', 'ceo'].includes(r.value));
        setSelectedRole(firstNonAdmin?.value || rolesRes.data[0].value);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedRole]);

  useEffect(() => { fetchData(); }, []);

  const getPageConfig = (role, pageKey) => {
    return rbacConfig[role]?.[pageKey] || { visible: true, fields: {} };
  };

  const isFieldVisible = (role, pageKey, fieldKey) => {
    const config = getPageConfig(role, pageKey);
    if (config.fields && fieldKey in config.fields) return config.fields[fieldKey];
    return true; // Default: visible
  };

  const handleTogglePage = async (pageKey) => {
    if (!selectedRole || ['superadmin', 'ceo'].includes(selectedRole)) return;
    const current = getPageConfig(selectedRole, pageKey);
    const newVisible = !current.visible;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/rbac/config`, {
        role: selectedRole, page: pageKey, visible: newVisible, fields: current.fields || {},
      }, { withCredentials: true });
      setRbacConfig(prev => ({
        ...prev,
        [selectedRole]: { ...prev[selectedRole], [pageKey]: { ...current, visible: newVisible } },
      }));
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleToggleField = async (pageKey, fieldKey) => {
    if (!selectedRole || ['superadmin', 'ceo'].includes(selectedRole)) return;
    const current = getPageConfig(selectedRole, pageKey);
    const currentFields = { ...(current.fields || {}) };
    const newVal = !isFieldVisible(selectedRole, pageKey, fieldKey);
    currentFields[fieldKey] = newVal;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/rbac/config`, {
        role: selectedRole, page: pageKey, visible: current.visible !== false, fields: currentFields,
      }, { withCredentials: true });
      setRbacConfig(prev => ({
        ...prev,
        [selectedRole]: { ...prev[selectedRole], [pageKey]: { visible: current.visible !== false, fields: currentFields } },
      }));
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const isAdminRole = ['superadmin', 'ceo'].includes(selectedRole);

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      {/* Role Selector */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center gap-4">
          <Label className="font-medium whitespace-nowrap">Select Role:</Label>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-64" data-testid="rbac-role-select"><SelectValue placeholder="Choose a role" /></SelectTrigger>
            <SelectContent>
              {roles.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label} {r.is_custom && <span className="text-blue-600">(Custom)</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdminRole && (
            <Badge className="bg-amber-100 text-amber-700">Superadmin/CEO always have full access</Badge>
          )}
          {saving && <SpinnerGap size={16} className="animate-spin text-blue-600" />}
        </CardContent>
      </Card>

      {/* Page + Field Matrix */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Page & Field Permissions</CardTitle>
          <CardDescription>Toggle page visibility and click to expand field-level controls. Changes save automatically.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {Object.entries(pageRegistry).map(([pageKey, pageDef]) => {
              const pageConfig = getPageConfig(selectedRole, pageKey);
              const pageVisible = pageConfig.visible !== false;
              const isExpanded = expandedPage === pageKey;
              const hiddenFieldCount = Object.values(pageConfig.fields || {}).filter(v => v === false).length;

              return (
                <div key={pageKey} data-testid={`rbac-page-${pageKey}`}>
                  <div className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                    {/* Expand toggle */}
                    <button
                      onClick={() => setExpandedPage(isExpanded ? null : pageKey)}
                      className="text-slate-400 hover:text-slate-700 shrink-0"
                    >
                      {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                    </button>
                    {/* Page name */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${pageVisible ? 'text-slate-900' : 'text-slate-400 line-through'}`}>{pageDef.label}</span>
                        <Badge variant="outline" className="text-[10px]">{pageDef.fields.length} fields</Badge>
                        {hiddenFieldCount > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">{hiddenFieldCount} hidden</Badge>
                        )}
                      </div>
                    </div>
                    {/* Page visibility toggle */}
                    <div className="flex items-center gap-2 shrink-0">
                      {pageVisible ? (
                        <span className="text-[10px] text-emerald-600 flex items-center gap-1"><Eye size={14} /> Visible</span>
                      ) : (
                        <span className="text-[10px] text-red-500 flex items-center gap-1"><EyeSlash size={14} /> Hidden</span>
                      )}
                      <Switch
                        checked={pageVisible}
                        onCheckedChange={() => handleTogglePage(pageKey)}
                        disabled={isAdminRole}
                        data-testid={`rbac-toggle-${pageKey}`}
                      />
                    </div>
                  </div>

                  {/* Expanded: Field-level toggles */}
                  {isExpanded && (
                    <div className="bg-slate-50/70 border-t border-slate-100 px-8 py-3">
                      <p className="text-xs text-slate-500 mb-3">Toggle individual fields/columns visible to <strong>{roles.find(r => r.value === selectedRole)?.label}</strong> on this page:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {pageDef.fields.map(fieldKey => {
                          const fieldVisible = isFieldVisible(selectedRole, pageKey, fieldKey);
                          return (
                            <label
                              key={fieldKey}
                              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${fieldVisible ? 'bg-white border border-slate-200 hover:border-blue-300' : 'bg-red-50 border border-red-200'}`}
                              data-testid={`rbac-field-${pageKey}-${fieldKey}`}
                            >
                              <Checkbox
                                checked={fieldVisible}
                                onCheckedChange={() => handleToggleField(pageKey, fieldKey)}
                                disabled={isAdminRole}
                              />
                              <span className={`text-xs font-medium ${fieldVisible ? 'text-slate-700' : 'text-red-600 line-through'}`}>
                                {fieldKey.replace(/_/g, ' ')}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ===== Tab 2: Roles Management ===== */
const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ value: '', label: '', department: 'sales' });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/rbac/roles`, { withCredentials: true });
      setRoles(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRoles(); }, []);

  const handleCreate = async () => {
    if (!form.value || !form.label) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/rbac/roles`, form, { withCredentials: true });
      setShowModal(false);
      setForm({ value: '', label: '', department: 'sales' });
      fetchRoles();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete custom role "${role.label}"? This will also remove its RBAC config.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/rbac/roles/${role.id}`, { withCredentials: true });
      fetchRoles();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-['Outfit']">All Roles</CardTitle>
            <CardDescription>Built-in roles + your custom roles. Custom roles can be deleted.</CardDescription>
          </div>
          <Button onClick={() => { setForm({ value: '', label: '', department: 'sales' }); setShowModal(true); }} data-testid="create-role-btn">
            <Plus size={16} className="mr-2" /> Create Custom Role
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Role Key</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {roles.map((r) => (
                  <tr key={r.value} className="hover:bg-slate-50" data-testid={`role-row-${r.value}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.value}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.label}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-[10px]">{r.department || '-'}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {r.is_custom ? (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px]">Custom</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]">Built-in</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.is_custom && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete(r)}>
                          <Trash size={16} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Role Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Role</DialogTitle>
            <DialogDescription>Create a new role. You can then assign page and field permissions to it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role Key *</Label>
              <Input
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g. sales_coordinator"
                data-testid="input-role-key"
              />
              <p className="text-xs text-slate-400">Lowercase, underscores only. Used internally.</p>
            </div>
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Sales Coordinator" data-testid="input-role-label" />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="select-role-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="recruiting">Recruiting</SelectItem>
                  <SelectItem value="both">Sales & Recruiting</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" disabled={saving || !form.value || !form.label}>
              {saving && <SpinnerGap size={16} className="animate-spin mr-2" />} Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ===== Tab 3: Custom Fields ===== */
const CustomFieldsTab = () => {
  const [fields, setFields] = useState([]);
  const [pageRegistry, setPageRegistry] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ field_key: '', field_label: '', page: '', field_type: 'text', options: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fieldsRes, registryRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/rbac/custom-fields`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/rbac/page-registry`, { withCredentials: true }),
      ]);
      setFields(fieldsRes.data);
      setPageRegistry(registryRes.data.pages || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.field_key || !form.field_label || !form.page) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        options: form.field_type === 'select' ? form.options.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API_URL}/api/admin/rbac/custom-fields`, payload, { withCredentials: true });
      setShowModal(false);
      setForm({ field_key: '', field_label: '', page: '', field_type: 'text', options: '' });
      fetchData();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (field) => {
    if (!window.confirm(`Delete custom field "${field.field_label}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/rbac/custom-fields/${field.id}`, { withCredentials: true });
      fetchData();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-['Outfit']">Custom Fields</CardTitle>
            <CardDescription>Add new data fields to any page. These appear in the field-level permissions and can be controlled per role.</CardDescription>
          </div>
          <Button onClick={() => { setForm({ field_key: '', field_label: '', page: '', field_type: 'text', options: '' }); setShowModal(true); }} data-testid="create-field-btn">
            <Plus size={16} className="mr-2" /> Add Custom Field
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {fields.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Columns size={48} className="mx-auto mb-2 opacity-30" />
              <p>No custom fields created yet</p>
              <p className="text-sm">Add fields to extend data on any page</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Field Key</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Label</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Page</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Options</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {fields.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50" data-testid={`field-row-${f.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{f.field_key}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{f.field_label}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-[10px]">{pageRegistry[f.page]?.label || f.page}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]">{f.field_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{f.options?.length > 0 ? f.options.join(', ') : '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete(f)}>
                          <Trash size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Field Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
            <DialogDescription>Create a new field that will appear on the selected page and can be controlled via role permissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Field Key *</Label>
              <Input
                value={form.field_key}
                onChange={(e) => setForm({ ...form, field_key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g. client_rating"
                data-testid="input-field-key"
              />
              <p className="text-xs text-slate-400">Lowercase, underscores only. Used internally.</p>
            </div>
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input value={form.field_label} onChange={(e) => setForm({ ...form, field_label: e.target.value })} placeholder="e.g. Client Rating" data-testid="input-field-label" />
            </div>
            <div className="space-y-2">
              <Label>Page *</Label>
              <Select value={form.page} onValueChange={(v) => setForm({ ...form, page: v })}>
                <SelectTrigger data-testid="select-field-page"><SelectValue placeholder="Select page" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(pageRegistry).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select value={form.field_type} onValueChange={(v) => setForm({ ...form, field_type: v })}>
                <SelectTrigger data-testid="select-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="select">Select (Dropdown)</SelectItem>
                  <SelectItem value="boolean">Yes/No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.field_type === 'select' && (
              <div className="space-y-2">
                <Label>Options (comma-separated)</Label>
                <Input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} placeholder="Option A, Option B, Option C" data-testid="input-field-options" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" disabled={saving || !form.field_key || !form.field_label || !form.page}>
              {saving && <SpinnerGap size={16} className="animate-spin mr-2" />} Create Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ===== Tab 4: Security (Existing IP/MAC) ===== */
const SecurityTab = () => {
  const [config, setConfig] = useState({ ip_restriction_enabled: false, mac_restriction_enabled: false });
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [macWhitelist, setMacWhitelist] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showIPModal, setShowIPModal] = useState(false);
  const [showMACModal, setShowMACModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [ipForm, setIpForm] = useState({ ip_address: '', description: '', is_active: true });
  const [macForm, setMacForm] = useState({ mac_address: '', description: '', is_active: true });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, ipRes, macRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/access-control/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/access-control/ip-whitelist`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/access-control/mac-whitelist`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/access-control/login-history?limit=50`, { withCredentials: true }),
      ]);
      setConfig(configRes.data);
      setIpWhitelist(ipRes.data);
      setMacWhitelist(macRes.data);
      setLoginHistory(historyRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/access-control/config`, config, { withCredentials: true });
      alert('Configuration saved!');
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleAddIP = async () => {
    setSaving(true);
    try {
      if (editEntry) {
        await axios.put(`${API_URL}/api/admin/access-control/ip-whitelist/${editEntry.id}`, ipForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/access-control/ip-whitelist`, ipForm, { withCredentials: true });
      }
      setShowIPModal(false); setEditEntry(null); setIpForm({ ip_address: '', description: '', is_active: true }); fetchData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const handleDeleteIP = async (id) => {
    if (!window.confirm('Delete this IP?')) return;
    try { await axios.delete(`${API_URL}/api/admin/access-control/ip-whitelist/${id}`, { withCredentials: true }); fetchData(); }
    catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><ShieldCheck size={24} className="text-blue-600" /> Security Settings</CardTitle>
          <CardDescription>Enable restrictions to control who can access the portal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Globe size={24} className="text-slate-600" />
              <div><p className="font-medium">IP Address Restriction</p><p className="text-sm text-slate-500">Only allow logins from whitelisted IPs</p></div>
            </div>
            <Switch checked={config.ip_restriction_enabled} onCheckedChange={(c) => setConfig({ ...config, ip_restriction_enabled: c })} data-testid="toggle-ip-restriction" />
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Desktop size={24} className="text-slate-600" />
              <div><p className="font-medium">MAC Address Restriction</p><p className="text-sm text-slate-500">Only allow logins from whitelisted devices</p></div>
            </div>
            <Switch checked={config.mac_restriction_enabled} onCheckedChange={(c) => setConfig({ ...config, mac_restriction_enabled: c })} data-testid="toggle-mac-restriction" />
          </div>
          <Button onClick={handleSaveConfig} className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
            {saving && <SpinnerGap size={16} className="animate-spin mr-2" />} Save Configuration
          </Button>
        </CardContent>
      </Card>

      {/* IP Whitelist */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div><CardTitle className="text-lg font-['Outfit']">IP Whitelist</CardTitle></div>
          <Button onClick={() => { setEditEntry(null); setIpForm({ ip_address: '', description: '', is_active: true }); setShowIPModal(true); }} data-testid="add-ip-btn"><Plus size={16} className="mr-2" /> Add IP</Button>
        </CardHeader>
        <CardContent className="p-0">
          {ipWhitelist.length === 0 ? (
            <div className="p-6 text-center text-slate-500"><WifiHigh size={32} className="mx-auto mb-1 opacity-30" /><p className="text-sm">No IPs whitelisted</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b"><tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">IP</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Description</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Status</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {ipWhitelist.map(e => (
                  <tr key={e.id}><td className="px-4 py-2 font-mono text-xs">{e.ip_address}</td><td className="px-4 py-2 text-slate-500">{e.description || '-'}</td><td className="px-4 py-2"><Badge className={e.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{e.is_active ? 'Active' : 'Inactive'}</Badge></td><td className="px-4 py-2 text-right"><Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDeleteIP(e.id)}><Trash size={14} /></Button></td></tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Login History */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-lg font-['Outfit']">Login History</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loginHistory.length === 0 ? (
            <div className="p-6 text-center text-slate-500"><Clock size={32} className="mx-auto mb-1 opacity-30" /><p className="text-sm">No login history</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b"><tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Time</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Email</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">IP</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {loginHistory.map((e, i) => (
                  <tr key={i}><td className="px-4 py-2 text-xs">{e.timestamp ? new Date(e.timestamp).toLocaleString() : '-'}</td><td className="px-4 py-2 font-medium">{e.email}</td><td className="px-4 py-2 font-mono text-xs">{e.ip_address}</td><td className="px-4 py-2">{e.status === 'success' ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle size={14} /> Success</span> : <span className="text-red-700 flex items-center gap-1"><XCircle size={14} /> {e.status}</span>}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Add IP Modal */}
      <Dialog open={showIPModal} onOpenChange={setShowIPModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editEntry ? 'Edit IP' : 'Add IP'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>IP Address *</Label><Input value={ipForm.ip_address} onChange={(e) => setIpForm({ ...ipForm, ip_address: e.target.value })} placeholder="192.168.1.1" data-testid="input-ip-address" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={ipForm.description} onChange={(e) => setIpForm({ ...ipForm, description: e.target.value })} placeholder="Office network" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIPModal(false)}>Cancel</Button>
            <Button onClick={handleAddIP} className="bg-blue-600 hover:bg-blue-700" disabled={saving || !ipForm.ip_address}>{saving && <SpinnerGap size={16} className="animate-spin mr-2" />} {editEntry ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessControlPage;
